/**
 * Polls Service
 * API client functions for polls/voting functionality.
 *
 * Uses the shared `apiClient` (audit #6/#34): relative `/api` paths through the
 * dev proxy, automatic Bearer-token injection, and 401-refresh handling. Do NOT
 * reintroduce raw axios or a hand-passed token here.
 */

import apiClient from './api.client';
import type {
  ApiResponse,
  Poll,
  CreatePollRequest,
  VotePollRequest,
} from '../types/api.types';

/**
 * Create a new poll
 */
export async function createPoll(pollData: CreatePollRequest): Promise<Poll> {
  const response = await apiClient.post<ApiResponse<{ poll: Poll }>>(
    '/polls',
    pollData
  );

  if (!response.data.data) {
    throw new Error(response.data.error || 'Failed to create poll');
  }

  return response.data.data.poll;
}

/**
 * Get poll by ID with results
 */
export async function getPoll(pollId: string): Promise<Poll> {
  const response = await apiClient.get<ApiResponse<{ poll: Poll }>>(
    `/polls/${pollId}`
  );

  if (!response.data.data) {
    throw new Error(response.data.error || 'Failed to get poll');
  }

  return response.data.data.poll;
}

/**
 * Vote on a poll
 */
export async function voteOnPoll(
  pollId: string,
  voteData: VotePollRequest
): Promise<Poll> {
  const response = await apiClient.post<ApiResponse<{ poll: Poll }>>(
    `/polls/${pollId}/vote`,
    voteData
  );

  if (!response.data.data) {
    throw new Error(response.data.error || 'Failed to vote on poll');
  }

  return response.data.data.poll;
}

/**
 * Change vote on a poll
 */
export async function changeVote(
  pollId: string,
  voteData: VotePollRequest
): Promise<Poll> {
  const response = await apiClient.patch<ApiResponse<{ poll: Poll }>>(
    `/polls/${pollId}/vote`,
    voteData
  );

  if (!response.data.data) {
    throw new Error(response.data.error || 'Failed to change vote');
  }

  return response.data.data.poll;
}

/**
 * Close a poll (creator or admin only)
 */
export async function closePoll(pollId: string): Promise<Poll> {
  const response = await apiClient.post<ApiResponse<{ poll: Poll }>>(
    `/polls/${pollId}/close`,
    {}
  );

  if (!response.data.data) {
    throw new Error(response.data.error || 'Failed to close poll');
  }

  return response.data.data.poll;
}

/**
 * Get poll results
 */
export async function getPollResults(pollId: string): Promise<Poll> {
  const response = await apiClient.get<ApiResponse<{ poll: Poll }>>(
    `/polls/${pollId}/results`
  );

  if (!response.data.data) {
    throw new Error(response.data.error || 'Failed to get poll results');
  }

  return response.data.data.poll;
}
